import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { API_URL as API } from '../utils/config';

const AdminOrders = () => {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (!user || !user.is_admin) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchOrders();
  }, [user, navigate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/orders`, { headers });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${newStatus}`, {}, { headers });
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update order status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('no-NO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24 flex items-center justify-center">
        <p className="text-[#8A8A8A]">Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-lg font-sans font-bold uppercase tracking-tight text-[#1A1A1A]">
            Order Management
          </h1>
          <button
            onClick={() => navigate('/admin')}
            className="bg-transparent border border-[#1A1A1A] text-[#1A1A1A] px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-[#1A1A1A] hover:text-white transition-all"
          >
            Back to Products
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
            <p className="text-[#8A8A8A]">No orders yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setSelectedOrder(selectedOrder?.id === order.id ? null : order)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <h3 className="text-lg font-sans font-bold text-[#1A1A1A]">
                          Order #{order.id.slice(0, 8)}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider ${
                          order.status === 'completed' ? 'bg-green-100 text-green-800' :
                          order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                      <p className="text-sm text-[#8A8A8A]">
                        <span className="font-medium">Customer:</span> {order.email}
                      </p>
                      <p className="text-sm text-[#8A8A8A]">
                        <span className="font-medium">Date:</span> {formatDate(order.created_at)}
                      </p>
                      <p className="text-sm text-[#1A1A1A] font-bold mt-2">
                        Total: {order.total_amount} {order.currency.toUpperCase()}
                      </p>
                    </div>
                    <div className="text-right">
                      <button className="text-sm text-[#1A1A1A] hover:text-[#C6A87C]">
                        {selectedOrder?.id === order.id ? '▼ Hide Details' : '▶ View Details'}
                      </button>
                    </div>
                  </div>
                </div>

                {selectedOrder?.id === order.id && (
                  <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-[#1A1A1A] mb-4">Order Items:</h4>
                    <div className="space-y-3 mb-6">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex justify-between items-center bg-white p-4 rounded">
                          <div>
                            <p className="font-medium text-[#1A1A1A]">{item.name || item.product_name}</p>
                            <p className="text-sm text-[#8A8A8A]">Quantity: {item.quantity}</p>
                            {item.shade && <p className="text-xs text-[#8A8A8A]">Shade: {item.shade}</p>}
                          </div>
                          <p className="font-bold text-[#1A1A1A]">{item.price * item.quantity} NOK</p>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-bold uppercase tracking-wider text-[#1A1A1A] mb-3">Update Status:</h4>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => updateOrderStatus(order.id, 'processing')}
                          className="px-4 py-2 text-xs uppercase tracking-wider bg-yellow-600 text-white hover:bg-yellow-700 transition-colors"
                        >
                          Processing
                        </button>
                        <button
                          onClick={() => updateOrderStatus(order.id, 'shipped')}
                          className="px-4 py-2 text-xs uppercase tracking-wider bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                        >
                          Shipped
                        </button>
                        <button
                          onClick={() => updateOrderStatus(order.id, 'completed')}
                          className="px-4 py-2 text-xs uppercase tracking-wider bg-green-600 text-white hover:bg-green-700 transition-colors"
                        >
                          Completed
                        </button>
                        <button
                          onClick={() => updateOrderStatus(order.id, 'cancelled')}
                          className="px-4 py-2 text-xs uppercase tracking-wider bg-red-600 text-white hover:bg-red-700 transition-colors"
                        >
                          Cancelled
                        </button>
                      </div>
                    </div>

                    {order.payment_session_id && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs text-[#8A8A8A]">
                          Payment Session: {order.payment_session_id}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
